import styled from "styled-components";

import { enumValue, formatPrice, humanizeKey } from "../../utils/format";
import { photoUrlFromManifest } from "../../utils/photos";
import CoverPreview from "../CoverPreview/CoverPreview";

/**
 * A live, client-side render of the full report document — the WYSIWYG half of
 * the editor. Mirrors the worker's HTML templates (section headers, pricing
 * hero, comp cards, records tables, photo grids) so what the agent sees here is
 * recognizably the same document the PDF/PPTX exports produce, but re-renders
 * instantly from the editor's local state on every keystroke.
 *
 * Everything degrades gracefully when a field is absent, exactly like the
 * server templates do.
 */

/** @typedef {Record<string, any>} ReportRecord */
/** @typedef {Record<string, any>} PhotoManifest */

// ---------------------------------------------------------------------------
// The sheet: a document page, distinct from the editing chrome around it.
// ---------------------------------------------------------------------------
const Sheet = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8e6;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(11, 28, 30, 0.08);
  padding: 28px 30px 34px;

  @media (max-width: 768px) {
    padding: 20px 18px 26px;
  }
`;

const SectionBlock = styled.section`
  margin-top: 30px;
  border-radius: 10px;
  /* Mobile tab-jumps use scrollIntoView(start) on the page — clear the fixed
     navbar. Desktop rail scrolls are positioned manually and ignore this. */
  scroll-margin-top: 96px;
  /* Focus-sync flash: a soft accent ring that fades out, so the eye lands on
     the section that just scrolled into view (or was just rebuilt by AI). */
  transition: box-shadow 0.6s ease;
  box-shadow: ${(p) => (p.$flash ? `0 0 0 3px ${p.$accent}55` : "0 0 0 0 rgba(0, 0, 0, 0)")};
`;

const SectionHeading = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0b1c1e;
  border-bottom: 2px solid ${(p) => p.$accent};
  padding-bottom: 8px;
  margin: 0 0 14px;
`;

// Clicking a section heading in the preview jumps to editing that part — the
// preview doubles as the document's table of contents.
const HeadingButton = styled.button`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #0e7e7a;
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const EditHint = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${HeadingButton}:hover &,
  ${HeadingButton}:focus-visible & {
    opacity: 1;
  }
`;

const SubHeading = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 18px 0 6px;
`;

const Paragraph = styled.p`
  font-size: 0.95rem;
  line-height: 1.65;
  color: #333333;
  margin: 0 0 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletList = styled.ul`
  margin: 0 0 10px;
  padding-left: 20px;
`;

const BulletItem = styled.li`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #333333;
  margin-bottom: 4px;
`;

// ---------------------------------------------------------------------------
// Minimal prose renderer: paragraphs on blank lines, "- " bullets, **bold**.
// The report prose is near-plain text with light markdown; this covers what
// the worker's md_prose filter renders without pulling in a markdown dep.
// ---------------------------------------------------------------------------
/** @param {unknown} text */
const renderInline = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: static inline fragments of a single string
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );
};

/** @param {{text?: unknown}} props */
const Prose = ({ text }) => {
  if (!text || !String(text).trim()) return null;
  const blocks = String(text)
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n").map((l) => l.trim());
        const isList = lines.every((l) => /^[-*•]\s+/.test(l));
        if (isList) {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: derived from a single prose string, re-rendered wholesale
            <BulletList key={bi}>
              {lines.map((l, li) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: see above
                <BulletItem key={li}>{renderInline(l.replace(/^[-*•]\s+/, ""))}</BulletItem>
              ))}
            </BulletList>
          );
        }
        // biome-ignore lint/suspicious/noArrayIndexKey: see above
        return <Paragraph key={bi}>{renderInline(lines.join(" "))}</Paragraph>;
      })}
    </>
  );
};

/** @param {{items?: unknown[]}} props */
const Bullets = ({ items }) => {
  const list = (items || []).map((i) => String(i || "").trim()).filter(Boolean);
  if (!list.length) return null;
  return (
    <BulletList>
      {list.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: free-text list items, replaced wholesale on edit
        <BulletItem key={i}>{renderInline(item)}</BulletItem>
      ))}
    </BulletList>
  );
};

/** A labeled prose block, skipped entirely when empty. */
/** @param {{label: React.ReactNode, text?: unknown}} props */
const Labeled = ({ label, text }) =>
  text && String(text).trim() ? (
    <>
      <SubHeading>{label}</SubHeading>
      <Prose text={text} />
    </>
  ) : null;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
/** @param {unknown} n */
const fmtInt = (n) => {
  const v = Number(n);
  return v && !Number.isNaN(v) ? Math.round(v).toLocaleString() : "";
};

// ---------------------------------------------------------------------------
// Comparable analysis (the section with real structure)
// ---------------------------------------------------------------------------
const PricingHero = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: flex-start;
  background: #f4faf9;
  border: 1px solid #dcecea;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 14px;
`;

const HeroLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
`;

const HeroRange = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0b1c1e;
  margin: 2px 0 6px;
`;

const VerdictPill = styled.span`
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  background: #e6f4f3;
  color: #0b605d;
`;

const HeroAnchor = styled.div`
  font-size: 0.85rem;
  color: #4a4a4a;
  margin-top: 8px;
`;

const PpsfBox = styled.div`
  flex: 1;
  min-width: 220px;
`;

const PpsfLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 0.8rem;
  color: #374151;
`;

const PpsfTrack = styled.span`
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: #e5eeec;
  overflow: hidden;
`;

const PpsfFill = styled.span`
  display: block;
  height: 100%;
  border-radius: 999px;
  background: ${(p) => (p.$market ? "#9ab8b4" : p.$accent)};
`;

const PpsfValue = styled.span`
  flex: 0 0 44px;
  text-align: right;
  font-weight: 700;
`;

const CompCard = styled.div`
  border: 1px solid #e9ecef;
  border-left: 4px solid
    ${(p) => (p.$status === "sold" ? "#27ae60" : p.$status === "pending" ? "#f39c12" : p.$accent)};
  border-radius: 8px;
  background: #fdfdfd;
  padding: 13px 15px;
  margin-bottom: 12px;
`;

const CompTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const CompAddress = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #0b1c1e;
`;

const CompPrice = styled.div`
  font-weight: 700;
  font-size: 0.98rem;
  color: #1f7a45;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
  background: ${(p) =>
    p.$status === "sold" ? "#e8f5e9" : p.$status === "pending" ? "#fff3e0" : "#e3f2fd"};
  color: ${(p) =>
    p.$status === "sold" ? "#2e7d32" : p.$status === "pending" ? "#f57c00" : "#1976d2"};
`;

const CompDetails = styled.div`
  font-size: 0.85rem;
  color: #495057;
  margin-top: 4px;
`;

const CompNote = styled.div`
  font-size: 0.83rem;
  line-height: 1.5;
  color: #6c757d;
  margin-top: 6px;
`;

const RecordsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-bottom: 10px;

  th {
    text-align: left;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    border-bottom: 1px solid #e5e7eb;
    padding: 6px 8px;
  }

  td {
    padding: 7px 8px;
    border-bottom: 1px solid #f1f3f2;
    color: #374151;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

/** @param {ReportRecord} comp */
const compDetailsLine = (comp) => {
  const bits = [];
  if (comp.bedrooms) bits.push(`${comp.bedrooms} bd`);
  if (comp.bathrooms) bits.push(`${comp.bathrooms} ba`);
  if (comp.square_feet) bits.push(`${fmtInt(comp.square_feet)} sqft`);
  if (comp.year_built) bits.push(`built ${comp.year_built}`);
  if (comp.price_per_sqft) bits.push(`$${Math.round(comp.price_per_sqft)}/SF`);
  if (comp.days_on_market) bits.push(`${comp.days_on_market} days on market`);
  return bits.join(" · ");
};

// A comparable's status as the worker's stylesheet knows it. A listing that
// left the market is "off market", never a sale (Trello #56).
const COMP_STATUS_LABELS = {
  sold: "Sold",
  pending: "Pending",
  under_contract: "Under contract",
  active: "Active",
  off_market: "Off market",
};
/** @param {unknown} value */
export const compStatusSlug = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  if (!text) return "active";
  if (/off[ _-]?market|removed|withdrawn|delisted|expired/.test(text)) return "off_market";
  if (/sold|closed/.test(text)) return "sold";
  if (/under contract|contingent/.test(text)) return "under_contract";
  if (/pending/.test(text)) return "pending";
  return "active";
};

/** @param {{section: ReportRecord, accent: string}} props */
const ComparablePreview = ({ section, accent }) => {
  const comps = /** @type {ReportRecord[]} */ (section.comparable_properties || []);
  const supporting = /** @type {ReportRecord[]} */ (section.supporting_comparables || []);
  const low = section.suggested_range_low;
  const high = section.suggested_range_high;
  const subjPpsf = Number(section.subject_price_per_sqft) || 0;
  const mktPpsf = Number(section.comp_avg_price_per_sqft) || 0;
  const ppsfMax = Math.max(subjPpsf, mktPpsf) || 1;
  return (
    <>
      {low && high ? (
        <PricingHero>
          <div>
            <HeroLabel>Suggested list range</HeroLabel>
            <HeroRange>
              {formatPrice(low)} – {formatPrice(high)}
            </HeroRange>
            {section.pricing_verdict ? <VerdictPill>{section.pricing_verdict}</VerdictPill> : null}
            {section.anchor_price ? (
              <HeroAnchor>
                Recommended anchor list price <strong>{formatPrice(section.anchor_price)}</strong>
              </HeroAnchor>
            ) : null}
          </div>
          {subjPpsf && mktPpsf ? (
            <PpsfBox>
              <HeroLabel>Price per sq ft</HeroLabel>
              <PpsfLine>
                <span style={{ flex: "0 0 52px" }}>Subject</span>
                <PpsfTrack>
                  <PpsfFill $accent={accent} style={{ width: `${(subjPpsf / ppsfMax) * 100}%` }} />
                </PpsfTrack>
                <PpsfValue>${Math.round(subjPpsf)}</PpsfValue>
              </PpsfLine>
              <PpsfLine>
                <span style={{ flex: "0 0 52px" }}>Market</span>
                <PpsfTrack>
                  <PpsfFill $market style={{ width: `${(mktPpsf / ppsfMax) * 100}%` }} />
                </PpsfTrack>
                <PpsfValue>${Math.round(mktPpsf)}</PpsfValue>
              </PpsfLine>
            </PpsfBox>
          ) : null}
        </PricingHero>
      ) : null}

      <Bullets items={section.pricing_justification} />

      {comps.length > 0 && (
        <>
          <SubHeading>The comparables</SubHeading>
          {comps.map((comp, i) => {
            const status = compStatusSlug(comp.status);
            return (
              <CompCard key={`${comp.address}-${i}`} $status={status} $accent={accent}>
                <CompTopRow>
                  <CompAddress>{comp.address}</CompAddress>
                  <CompPrice>
                    {formatPrice(comp.price) || "Price unavailable"}
                    <StatusBadge $status={status}>
                      {COMP_STATUS_LABELS[status] || status}
                    </StatusBadge>
                  </CompPrice>
                </CompTopRow>
                {compDetailsLine(comp) && <CompDetails>{compDetailsLine(comp)}</CompDetails>}
                {comp.comparison_summary && <CompNote>{comp.comparison_summary}</CompNote>}
              </CompCard>
            );
          })}
        </>
      )}

      {supporting.length > 0 && (
        <>
          <SubHeading>Additional comparables</SubHeading>
          <TableScroll>
            <RecordsTable>
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Price</th>
                  <th>$/SF</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {supporting.map((sc, i) => (
                  <tr key={`${sc.address}-${i}`}>
                    <td>{sc.address}</td>
                    <td>{formatPrice(sc.price) || "—"}</td>
                    <td>{sc.price_per_sqft ? `$${Math.round(sc.price_per_sqft)}` : "—"}</td>
                    <td>{humanizeKey(sc.status || "active")}</td>
                  </tr>
                ))}
              </tbody>
            </RecordsTable>
          </TableScroll>
        </>
      )}

      <Labeled label="Market positioning" text={section.market_positioning} />
      <Labeled label="Current market conditions" text={section.current_market_conditions} />
      <Labeled label="Pricing strategy" text={section.pricing_strategy} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------
const PreviewPhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin: 6px 0 10px;
`;

const PreviewPhotoFigure = styled.figure`
  margin: 0;
  position: relative;
`;

const PreviewPhoto = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 8px;
  display: block;
`;

const PreviewPhotoCaption = styled.figcaption`
  font-size: 0.74rem;
  color: #6b7280;
  margin-top: 3px;
  line-height: 1.35;
`;

const PhotoOrderTag = styled.span`
  position: absolute;
  top: 6px;
  left: 6px;
  min-width: 20px;
  height: 20px;
  border-radius: 999px;
  background: rgba(11, 28, 30, 0.78);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
`;

/** @param {PhotoManifest} manifest @param {unknown} p */
const photoUrl = (manifest, p) => photoUrlFromManifest(manifest, p);

// A recommendation card — the photo sections' visible advice in the document.
const RecCard = styled.div`
  border: 1px solid #e9ecef;
  border-left: 3px solid ${(p) => p.$accent};
  border-radius: 8px;
  background: #fdfdfd;
  padding: 12px 14px;
  margin-bottom: 10px;
`;

const RecTitle = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: #0b1c1e;
`;

const RecMeta = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin: 6px 0 8px;
`;

const RecChip = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f4;
  color: #4b5563;
`;

const RecText = styled.p`
  font-size: 0.9rem;
  line-height: 1.55;
  color: #333333;
  margin: 0 0 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

/** @param {unknown} v */
/** @param {{section: ReportRecord, manifest: PhotoManifest, accent: string}} props */
const PhotoAnalysisPreview = ({ section, manifest, accent }) => {
  const a = section.assessment || {};
  const photos = /** @type {unknown[]} */ (a.photos || []);
  const captions = /** @type {string[]} */ (a.photo_captions || []);
  const recs = /** @type {ReportRecord[]} */ (
    Array.isArray(a.recommendations) ? a.recommendations : []
  );
  return (
    <>
      {photos.length > 0 && (
        <PreviewPhotoGrid>
          {photos.map((p, i) => {
            const url = photoUrl(manifest, p);
            if (!url) return null;
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: photos are positional
              <PreviewPhotoFigure key={`${p}-${i}`}>
                <PreviewPhoto src={url} alt={captions[i] || ""} loading="lazy" />
                {captions[i] ? <PreviewPhotoCaption>{captions[i]}</PreviewPhotoCaption> : null}
              </PreviewPhotoFigure>
            );
          })}
        </PreviewPhotoGrid>
      )}
      {a.overview_text ? (
        <>
          <SubHeading>Analysis overview</SubHeading>
          <Prose text={a.overview_text} />
        </>
      ) : null}
      {recs.map((rec, i) => (
        <RecCard key={`${rec.title}-${i}`} $accent={accent}>
          {rec.title ? <RecTitle>{rec.title}</RecTitle> : null}
          <RecMeta>
            {["impact", "cost", "effort"].map((f) =>
              enumValue(rec[f]) ? (
                <RecChip key={f}>
                  {humanizeKey(f)}: {enumValue(rec[f])}
                </RecChip>
              ) : null
            )}
          </RecMeta>
          {rec.action ? <RecText>{rec.action}</RecText> : null}
          {rec.rationale ? <RecText>{rec.rationale}</RecText> : null}
        </RecCard>
      ))}
    </>
  );
};

/** @param {{section: ReportRecord, manifest: PhotoManifest}} props */
const PhotoSequencePreview = ({ section, manifest }) => {
  const strategy = section.strategy || {};
  const sequence = /** @type {unknown[]} */ (strategy.complete_sequence || []);
  return (
    <>
      {sequence.length > 0 && (
        <PreviewPhotoGrid>
          {sequence.map((p, i) => {
            const url = photoUrl(manifest, p);
            if (!url) return null;
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: photos are positional in the sequence
              <PreviewPhotoFigure key={`${p}-${i}`}>
                <PreviewPhoto src={url} alt="" loading="lazy" />
                <PhotoOrderTag>{strategy.lead_image === p ? "Lead" : i + 1}</PhotoOrderTag>
              </PreviewPhotoFigure>
            );
          })}
        </PreviewPhotoGrid>
      )}
      <Labeled label="Lead image" text={strategy.lead_image_rationale} />
      <Labeled label="Sequence rationale" text={strategy.sequence_rationale} />
      <Labeled label="Strategy notes" text={strategy.strategy_notes} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Records tables (tax / price history)
// ---------------------------------------------------------------------------
/** @param {{section: ReportRecord}} props */
const TaxRecordsPreview = ({ section }) => (
  <>
    {(section.records || []).length > 0 && (
      <TableScroll>
        <RecordsTable>
          <thead>
            <tr>
              <th>Year</th>
              <th>Tax assessment</th>
              <th>Tax paid</th>
            </tr>
          </thead>
          <tbody>
            {section.records.map((/** @type {ReportRecord} */ r, /** @type {number} */ i) => (
              <tr key={`${r.tax_year}-${i}`}>
                <td>{r.tax_year}</td>
                <td>{formatPrice(r.assessed_value) || "—"}</td>
                <td>{formatPrice(r.tax_paid) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </RecordsTable>
      </TableScroll>
    )}
    {section.property_tax_rate ? (
      <Paragraph>
        Effective property tax rate: <strong>{section.property_tax_rate}%</strong>
      </Paragraph>
    ) : null}
    <Prose text={section.trend_note} />
  </>
);

/** @param {{section: ReportRecord}} props */
const PriceHistoryPreview = ({ section }) => (
  <>
    {(section.records || []).length > 0 && (
      <TableScroll>
        <RecordsTable>
          <thead>
            <tr>
              <th>Date</th>
              <th>Event</th>
              <th>Price</th>
              <th>$/SF</th>
            </tr>
          </thead>
          <tbody>
            {section.records.map((/** @type {ReportRecord} */ r, /** @type {number} */ i) => (
              <tr key={`${r.date}-${i}`}>
                <td>{r.date || "—"}</td>
                <td>{r.event || "—"}</td>
                <td>{formatPrice(r.price) || "—"}</td>
                <td>{r.price_per_sqft ? `$${Math.round(r.price_per_sqft)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </RecordsTable>
      </TableScroll>
    )}
    <Prose text={section.trend_note} />
  </>
);

// ---------------------------------------------------------------------------
// Listing rewrite: the publishable copy gets a distinct "listing copy" frame.
// ---------------------------------------------------------------------------
const ListingCopyBlock = styled.blockquote`
  margin: 0 0 12px;
  padding: 14px 16px;
  border-left: 3px solid ${(p) => p.$accent};
  background: #fafcfb;
  border-radius: 0 8px 8px 0;
`;

/** @param {{section: ReportRecord, accent: string}} props */
const ListingRewritePreview = ({ section, accent }) => (
  <>
    {section.rewritten_description ? (
      <ListingCopyBlock $accent={accent}>
        <Prose text={section.rewritten_description} />
      </ListingCopyBlock>
    ) : null}
    {Array.isArray(section.neighborhood_highlights) &&
      section.neighborhood_highlights.length > 0 && (
        <>
          <SubHeading>Neighborhood highlights</SubHeading>
          <Bullets items={section.neighborhood_highlights} />
        </>
      )}
    <Labeled label="Target buyer appeal" text={section.target_buyer_appeal} />
  </>
);

/** @param {{section: ReportRecord}} props */
const ConclusionPreview = ({ section }) => (
  <>
    <Prose text={section.conclusion_text} />
    {Array.isArray(section.priority_actions) && section.priority_actions.length > 0 && (
      <>
        <SubHeading>Action plan</SubHeading>
        <BulletList>
          {section.priority_actions.map(
            (/** @type {ReportRecord} */ pa, /** @type {number} */ i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: ordered plan items, replaced wholesale
              <BulletItem key={i}>
                {renderInline(pa.action || "")}
                {pa.timeline ? ` — ${pa.timeline}` : ""}
                {pa.impact ? ` (${pa.impact})` : ""}
              </BulletItem>
            )
          )}
        </BulletList>
      </>
    )}
    <Labeled label="Market positioning strategy" text={section.market_positioning_strategy} />
    <Labeled label="Final recommendations" text={section.final_recommendations} />
    {Array.isArray(section.likely_outcomes) && section.likely_outcomes.length > 0 && (
      <>
        <SubHeading>Likely outcomes</SubHeading>
        <Bullets items={section.likely_outcomes} />
      </>
    )}
    {Array.isArray(section.watch_outs) && section.watch_outs.length > 0 && (
      <>
        <SubHeading>Watch-outs</SubHeading>
        <Bullets items={section.watch_outs} />
      </>
    )}
  </>
);

// Generic fallback: render whatever string / string-list fields the section has.
const INTERNAL_FIELDS = new Set(["__type__", "section_id", "title", "llm_response", "content"]);

/** @param {{section: ReportRecord}} props */
const GenericPreview = ({ section }) => (
  <>
    {Object.entries(section).map(([key, value]) => {
      if (INTERNAL_FIELDS.has(key)) return null;
      if (typeof value === "string" && value.trim()) {
        return <Labeled key={key} label={humanizeKey(key)} text={value} />;
      }
      if (Array.isArray(value) && value.length && value.every((v) => typeof v === "string")) {
        return (
          <div key={key}>
            <SubHeading>{humanizeKey(key)}</SubHeading>
            <Bullets items={value} />
          </div>
        );
      }
      return null;
    })}
  </>
);

// ---------------------------------------------------------------------------
// Section dispatch
// ---------------------------------------------------------------------------
/** @param {{section: ReportRecord, manifest: PhotoManifest, accent: string}} props */
const SectionBody = ({ section, manifest, accent }) => {
  switch (section.__type__) {
    case "ReportOverviewSection":
      return (
        <>
          <Prose text={section.brief_summary} />
          <Bullets items={section.key_highlights} />
        </>
      );
    case "ExecutiveSummarySection":
      return (
        <>
          <Prose text={section.summary} />
          {Array.isArray(section.key_assets) && section.key_assets.length > 0 && (
            <>
              <SubHeading>Key assets</SubHeading>
              <Bullets items={section.key_assets} />
            </>
          )}
          <Labeled label="Primary opportunity" text={section.primary_opportunity} />
          <Labeled label="Target buyer" text={section.target_buyer} />
        </>
      );
    case "NeighborhoodOverviewSection":
      return (
        <>
          <Prose text={section.overview_text} />
          <Labeled label="Property positioning" text={section.property_positioning} />
          <Labeled label="Amenities & lifestyle" text={section.amenities_lifestyle} />
          <Labeled label="Schools & education" text={section.schools_education} />
          <Labeled label="Market dynamics" text={section.market_dynamics} />
          <Labeled label="Commute & accessibility" text={section.commute_accessibility} />
        </>
      );
    case "ComparableAnalysisSection":
      return <ComparablePreview section={section} accent={accent} />;
    case "TaxRecordsSection":
      return <TaxRecordsPreview section={section} />;
    case "PriceHistorySection":
      return <PriceHistoryPreview section={section} />;
    case "PhotoAnalysisSection":
      return <PhotoAnalysisPreview section={section} manifest={manifest} accent={accent} />;
    case "PhotoSequenceSection":
      return <PhotoSequencePreview section={section} manifest={manifest} />;
    case "ListingRewriteSection":
      return <ListingRewritePreview section={section} accent={accent} />;
    case "ConclusionSection":
      return <ConclusionPreview section={section} />;
    default:
      return <GenericPreview section={section} />;
  }
};

const FooterNote = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  text-align: center;
  margin: 28px 0 0;
`;

/**
 * @param {{
 *   doc: Record<string, any>,
 *   sections: any[],
 *   branding?: Record<string, any>,
 *   showCover?: boolean,
 *   highlightId?: string | null,
 *   onSectionClick?: (sectionId: string) => void,
 * }} props
 */
const ReportPreview = ({
  doc,
  sections,
  branding = {},
  showCover = true,
  highlightId = null,
  onSectionClick,
}) => {
  const accent = branding.accent_color || "#0e7e7a";
  const manifest = doc.photo_manifest || {};
  return (
    <Sheet aria-label="Live document preview">
      {showCover && (
        <CoverPreview doc={doc} sections={sections} branding={branding} showViewDoc={false} />
      )}
      {(sections || []).map((section, index) => {
        const title = section.title || humanizeKey(section.__type__);
        return (
          <SectionBlock
            key={section.section_id || `${section.__type__}-${index}`}
            id={`preview-${section.section_id}`}
            $accent={accent}
            $flash={highlightId != null && highlightId === section.section_id}
          >
            <SectionHeading $accent={accent}>
              {onSectionClick ? (
                <HeadingButton
                  type="button"
                  title="Edit this part"
                  onClick={() => onSectionClick(section.section_id)}
                >
                  {title}
                  <EditHint aria-hidden="true">✎ Edit</EditHint>
                </HeadingButton>
              ) : (
                title
              )}
            </SectionHeading>
            <SectionBody section={section} manifest={manifest} accent={accent} />
          </SectionBlock>
        );
      })}
      <FooterNote>
        Live preview — your PDF, PowerPoint, and one-pager are rendered from exactly this content.
      </FooterNote>
    </Sheet>
  );
};

export default ReportPreview;
